import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Section from '../components/Section';
import Card from '../components/Card';
import Button from '../components/Button';
import { useAuth } from '../context/AuthContext';
import { getProfile } from '../api/profile';
import { PHILOSOPHY_CARDS, INNOVATION_STEPS, CLUB_BENEFITS } from '../constants/philosophy';
import { TEAM_MEMBERS } from '../constants/team';
import { PARTNERS } from '../constants/partners';
import styles from './Home.module.css';

// --- SVG-иконки для карточек философии ---
const ICONS = {
  shield: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
  ),
  data: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <line x1="18" y1="20" x2="18" y2="10" />
      <line x1="12" y1="20" x2="12" y2="4" />
      <line x1="6" y1="20" x2="6" y2="14" />
    </svg>
  ),
  person: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  ),
  science: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M9 3H5a2 2 0 0 0-2 2v4m6-6h10a2 2 0 0 1 2 2v4M9 3v11l6 7M9 3h6m0 0v11l-6 7" />
    </svg>
  ),
};

// --- Декоративная молекула (SVG, структурная химия) ---
const MoleculeBg = () => (
  <svg
    className={styles.heroBg}
    viewBox="0 0 420 420"
    fill="none"
    aria-hidden="true"
    xmlns="http://www.w3.org/2000/svg"
  >
    {/* Шестиугольное ароматическое кольцо (бензольный цикл) */}
    <polygon
      points="210,110 256,135 256,185 210,210 164,185 164,135"
      stroke="rgba(255,255,255,0.18)"
      strokeWidth="2"
      fill="none"
    />
    {/* Внутренний круг — ароматичность */}
    <circle cx="210" cy="160" r="28" stroke="rgba(255,255,255,0.10)" strokeWidth="1.5" fill="none" />

    {/* Атомы углов кольца */}
    {[
      [210, 110], [256, 135], [256, 185],
      [210, 210], [164, 185], [164, 135],
    ].map(([x, y], i) => (
      <circle key={i} cx={x} cy={y} r="5" fill="rgba(255,255,255,0.22)" />
    ))}

    {/* Боковые цепи */}
    {/* Верх */}
    <line x1="210" y1="110" x2="210" y2="68" stroke="rgba(255,255,255,0.15)" strokeWidth="2" />
    <circle cx="210" cy="60" r="8" stroke="rgba(255,255,255,0.20)" strokeWidth="1.5" fill="rgba(255,255,255,0.06)" />
    <text x="210" y="64" textAnchor="middle" fontSize="9" fill="rgba(255,255,255,0.35)" fontFamily="sans-serif">N</text>

    {/* Верх-право */}
    <line x1="256" y1="135" x2="290" y2="110" stroke="rgba(255,255,255,0.15)" strokeWidth="2" />
    <circle cx="298" cy="104" r="8" stroke="rgba(255,255,255,0.20)" strokeWidth="1.5" fill="rgba(255,255,255,0.06)" />
    <text x="298" y="108" textAnchor="middle" fontSize="9" fill="rgba(255,255,255,0.35)" fontFamily="sans-serif">O</text>

    {/* Право */}
    <line x1="256" y1="185" x2="300" y2="185" stroke="rgba(255,255,255,0.15)" strokeWidth="2" />
    <circle cx="310" cy="185" r="8" stroke="rgba(255,255,255,0.20)" strokeWidth="1.5" fill="rgba(255,255,255,0.06)" />
    <text x="310" y="189" textAnchor="middle" fontSize="9" fill="rgba(255,255,255,0.35)" fontFamily="sans-serif">C</text>
    <line x1="318" y1="185" x2="350" y2="165" stroke="rgba(255,255,255,0.12)" strokeWidth="1.5" />
    <circle cx="356" cy="162" r="6" fill="rgba(255,255,255,0.14)" />

    {/* Низ */}
    <line x1="210" y1="210" x2="210" y2="252" stroke="rgba(255,255,255,0.15)" strokeWidth="2" />
    <circle cx="210" cy="262" r="8" stroke="rgba(255,255,255,0.20)" strokeWidth="1.5" fill="rgba(255,255,255,0.06)" />
    <text x="210" y="266" textAnchor="middle" fontSize="9" fill="rgba(255,255,255,0.35)" fontFamily="sans-serif">O</text>
    <line x1="210" y1="270" x2="210" y2="300" stroke="rgba(255,255,255,0.12)" strokeWidth="1.5" />
    <circle cx="210" cy="308" r="6" fill="rgba(255,255,255,0.14)" />

    {/* Лево-низ */}
    <line x1="164" y1="185" x2="120" y2="210" stroke="rgba(255,255,255,0.15)" strokeWidth="2" />
    <circle cx="112" cy="215" r="8" stroke="rgba(255,255,255,0.20)" strokeWidth="1.5" fill="rgba(255,255,255,0.06)" />
    <text x="112" y="219" textAnchor="middle" fontSize="9" fill="rgba(255,255,255,0.35)" fontFamily="sans-serif">N</text>
    <line x1="104" y1="215" x2="72" y2="230" stroke="rgba(255,255,255,0.12)" strokeWidth="1.5" />
    <circle cx="65" cy="234" r="6" fill="rgba(255,255,255,0.14)" />

    {/* Лево-верх */}
    <line x1="164" y1="135" x2="130" y2="110" stroke="rgba(255,255,255,0.15)" strokeWidth="2" />
    <circle cx="122" cy="104" r="8" stroke="rgba(255,255,255,0.20)" strokeWidth="1.5" fill="rgba(255,255,255,0.06)" />
    <text x="122" y="108" textAnchor="middle" fontSize="9" fill="rgba(255,255,255,0.35)" fontFamily="sans-serif">C</text>
    <line x1="114" y1="104" x2="80" y2="88" stroke="rgba(255,255,255,0.12)" strokeWidth="1.5" />
    <circle cx="73" cy="84" r="6" fill="rgba(255,255,255,0.14)" />

    {/* Второе малое кольцо — пятичленный цикл (как в кофеине/пурине) */}
    <polygon
      points="298,104 330,90 348,115 330,138 298,125"
      stroke="rgba(255,255,255,0.13)"
      strokeWidth="1.5"
      fill="none"
    />
    {[
      [298, 104], [330, 90], [348, 115], [330, 138], [298, 125],
    ].map(([x, y], i) => (
      <circle key={i} cx={x} cy={y} r="4" fill="rgba(255,255,255,0.16)" />
    ))}
  </svg>
);

// --- Секция 1: Hero ---
const HeroSection = ({ isPaid }) => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const handlePrimaryClick = () => {
    if (isPaid) {
      navigate('/news');
    } else if (isAuthenticated) {
      navigate('/payment');
    } else {
      navigate('/club');
    }
  };

  const getPrimaryLabel = () => {
    if (isPaid) return 'Перейти в новости';
    if (isAuthenticated) return 'Оформить подписку — 2 000 ₽/мес';
    return 'Посмотреть программу клуба';
  };

  return (
    <section className={styles.hero}>
      <MoleculeBg />
      <div className={`container ${styles.heroContent}`}>
        <h1 className={styles.heroTitle}>
          Закрытый клуб<br />с Романом Терушкиным
        </h1>
        <p className={styles.heroDescription}>
          Хватит откладывать свое здоровье на потом<br />Пора разобраться вместе!
        </p>
        <p className={styles.heroSubtitle}>
          Онлайн - лекции, челленджи, разборы анализов по эндокринологии превентивной медицине от Романа Терушкина.
        </p>
        <div className={styles.heroActions}>
          <Button variant="primary" size="lg" onClick={handlePrimaryClick}>
            {getPrimaryLabel()}
          </Button>
        </div>
      </div>
    </section>
  );
};

// --- Секция 2: О владельце ---
const OwnerSection = () => (
  <Section bg="white">
    <div className={styles.owner}>
      <div className={styles.ownerPhoto}>
        <img
          src="/images/roman-terushkin.jpg"
          alt="Роман Терушкин — врач-эндокринолог, основатель проекта"
          className={styles.ownerPhotoImg}
        />
      </div>
      {/* Текстовый блок */}
      <div className={styles.ownerInfo}>
        <p className={styles.ownerLabel}>Основатель проекта</p>
        <h2 className={styles.ownerName}>Терушкин Роман Алексеевич </h2>
        <p className={styles.ownerRole}>Превентивный врач, врач-эндокринолог, нутрициолог, специалист по долголетию</p>
        <p className={styles.ownerDescription}>
          Автор книги «У гормонов на крючке», а также более 20 научных публикаций
          в области эндокринологии и биохимии. Более 10 лет я занимаюсь превентивной
          и функциональной медициной. За это время стало очевидно: большинство проблем
          со здоровьем можно было предотвратить, если действовать заблаговременно.
        </p>
        <div className={styles.ownerEducation}>
          <p className={styles.ownerEducationTitle}>Образование:</p>
          <ul className={styles.ownerEducationList}>
            <li>Выпускник РУДН по специальности «Лечебное дело»</li>
            <li>Ординатура на кафедре эндокринологии РУДН</li>
            <li>Повышение квалификации у коллег в Европе, Азии и США</li>
          </ul>
        </div>
        <p className={styles.ownerDescription}>
          Терушкин ОС — это система, созданная для того, чтобы знания о базовых механизмах
          здоровья стали доступными каждому, а забота о своём организме перестала быть сложной
          и запутанной. Здесь мы работаем с данными, а не с симптомами. Мы помогаем вам понять
          своё тело и принимать осознанные решения о здоровье.
        </p>
        <p className={styles.ownerDescription}>
          А также поддерживаем и соревнуемся друг с другом для достижения ещё лучших результатов.
        </p>
      </div>
    </div>
  </Section>
);

// --- Секция 3: Философия ---
const PhilosophySection = () => (
  <Section
    bg="gray"
    title="Наша философия, разработанная Терушкиным Р.А. и его командой"
    subtitle="Четыре принципа, на которых построен наш подход к здоровью и долголетию"
  >
    <div className={styles.philosophyGrid}>
      {PHILOSOPHY_CARDS.map((card) => (
        <Card key={card.id} variant="philosophy">
          <div className={styles.philosophyIcon}>{ICONS[card.icon]}</div>
          <h3 className={styles.philosophyTitle}>{card.title}</h3>
          <p className={styles.philosophyDesc}>{card.description}</p>
        </Card>
      ))}
    </div>
  </Section>
);

// --- Секция 4: Инновационный подход ---
const InnovationSection = () => (
  <Section
    bg="white"
    title="Как это работает"
    subtitle="Три составляющие клуба Терушкин ОС"
  >
    <div className={styles.steps}>
      {INNOVATION_STEPS.map((step, index) => (
        <div key={step.number} className={styles.step}>
          <div className={styles.stepNumber}>{step.number}</div>
          {index < INNOVATION_STEPS.length - 1 && (
            <div className={styles.stepConnector} aria-hidden="true" />
          )}
          <h3 className={styles.stepTitle}>{step.title}</h3>
          <p className={styles.stepDesc}>{step.description}</p>
        </div>
      ))}
    </div>
  </Section>
);

// --- Секция 5: Спикеры (превью) ---
const TeamPreviewSection = () => (
  <Section
    bg="gray"
    title="Спикеры клуба"
    subtitle="Специалисты, которые объединились ради одной цели — вашего здоровья"
  >
    <div className={styles.teamGrid}>
      {TEAM_MEMBERS.slice(0, 4).map((member) => (
        <div key={member.id} className={styles.teamCardWrap}>
          <Card variant="team" className={member.isFounder ? undefined : styles.teamCardBlurred}>
            <div className={styles.teamPhoto}>
              {member.isFounder ? (
                <img
                  src="/images/roman-terushkin-speaker.jpg"
                  alt={member.name}
                  className={styles.teamPhotoImg}
                />
              ) : (
                <svg viewBox="0 0 80 80" fill="none">
                  <circle cx="40" cy="28" r="18" fill="rgba(42,157,143,0.2)" />
                  <ellipse cx="40" cy="72" rx="28" ry="16" fill="rgba(42,157,143,0.12)" />
                </svg>
              )}
            </div>
            <h3 className={styles.teamName}>{member.name}</h3>
            <p className={styles.teamRole}>{member.role}</p>
            <p className={styles.teamDesc}>{member.description}</p>
          </Card>
          {!member.isFounder && <div className={styles.teamCardBadge}>Скоро</div>}
        </div>
      ))}
    </div>
    <div className={styles.teamAction}>
      <Link to="/team">
        <Button variant="outline" size="md">
          Подробнее о спикерах
        </Button>
      </Link>
    </div>
  </Section>
);

// --- Секция 6: Партнёры ---
const PartnersSection = () => (
  <Section bg="gray" title="Наши партнёры">
    <div className={styles.partnersGrid}>
      {PARTNERS.map((partner) => {
        const content = (
          <div key={partner.id} className={styles.partnerCard}>
            <img
              src={partner.logo}
              alt={partner.name}
              className={styles.partnerLogo}
            />
          </div>
        );
        return partner.url ? (
          <a
            key={partner.id}
            href={partner.url}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.partnerLink}
          >
            {content}
          </a>
        ) : (
          <div key={partner.id}>{content}</div>
        );
      })}
    </div>
  </Section>
);

// --- Секция 7: Приглашение в клуб ---
const ClubSection = ({ isPaid }) => (
  <section id="club" className={styles.club}>
    <div className="container">
      <div className={styles.clubContent}>
        <h2 className={styles.clubTitle}>Присоединяйтесь к клубу Терушкин ОС</h2>
        <p className={styles.clubSubtitle}>
          Станьте частью сообщества людей, которые берут здоровье под контроль
        </p>
        <ul className={styles.clubBenefits}>
          {CLUB_BENEFITS.map((benefit, i) => (
            <li key={i} className={styles.clubBenefit}>
              <span className={styles.clubBenefitIcon} aria-hidden="true">✓</span>
              {benefit}
            </li>
          ))}
        </ul>
        <Link to={isPaid ? '/news' : '/club'}>
          <Button variant="primary" size="lg">
            {isPaid ? 'Перейти в новости' : 'Оформить подписку — 2 000 ₽/мес'}
          </Button>
        </Link>
      </div>
    </div>
  </section>
);

// --- Главная страница ---
const Home = () => {
  const { token } = useAuth();
  const [isPaid, setIsPaid] = useState(false);

  useEffect(() => {
    if (!token) {
      setIsPaid(false);
      return;
    }
    getProfile(token)
      .then((profile) => {
        const payUntil = profile.pay_until ? new Date(profile.pay_until) : null;
        setIsPaid(payUntil !== null && payUntil > new Date());
      })
      .catch(() => setIsPaid(false));
  }, [token]);

  return (
    <>
      <HeroSection isPaid={isPaid} />
      <OwnerSection />
      <PhilosophySection />
      <InnovationSection />
      <TeamPreviewSection />
      <PartnersSection />
      <ClubSection isPaid={isPaid} />
    </>
  );
};

export default Home;
